/**
 * Passo a passo para conectar a agenda: celular, Google, Outlook e IAs.
 *
 * Fonte única dos textos: o Guia de conexão, "Integrações de IA" e
 * "Calendário no celular" leem daqui, para nunca dizerem coisas diferentes.
 * `{url}` vira o endereço do MCP e `{servidor}`, o host do CalDAV.
 * Os aplicativos de IA mudam o nome dos menus com frequência: por isso cada
 * passo diz o caminho mais comum e o guia lembra de procurar por "conector".
 */

/** Último passo de toda IA que faz login pelo ERP (OAuth). */
const PASSO_PERMITIR = "Abre uma página do Pharu ERP. Confira, marque o que a IA pode fazer e clique em Permitir. Se o ERP pedir, digite o código do seu app de verificação (o mesmo do login).";

export const DICA_DOS_MENUS = "Os aplicativos de IA mudam o nome dos menus de vez em quando. Se não achar, procure por “conector” ou “MCP” nas configurações.";

export const PASSOS_POR_IA = [
  {
    id: "claude", nome: "Claude",
    requisito: "Funciona no plano grátis (1 conector) e nos pagos, no site claude.ai e no app do computador.",
    passos: [
      "No Claude (site ou app), abra Configurações › Conectores e escolha adicionar um conector personalizado.",
      "Nome: Pharu ERP. URL: o endereço acima. Deixe o resto como veio e clique em Adicionar.",
      "Clique em Conectar.",
      PASSO_PERMITIR,
    ],
  },
  {
    id: "chatgpt", nome: "ChatGPT",
    requisito: "Precisa de um plano pago (Plus, Pro, Business ou Enterprise), no site chatgpt.com. Em conta de empresa, o administrador pode precisar liberar.",
    passos: [
      "No ChatGPT, abra Configurações › Aplicativos e conectores e ative o modo de desenvolvedor (em Avançado).",
      "Crie um conector com a URL acima e autenticação OAuth. Nome: Pharu ERP.",
      PASSO_PERMITIR,
      "Numa conversa, ligue o Pharu ERP no botão + para usar.",
    ],
  },
  {
    id: "gemini", nome: "Gemini", novo: true,
    requisito: "Precisa do plano Google AI Pro ou Ultra, numa conta Google pessoal (contas de empresa ou escola não têm). Faça pelo site gemini.google.com.",
    passos: [
      "No site gemini.google.com, abra Configurações › Apps conectados.",
      "Em Apps personalizados, clique em Adicionar. Nome: Pharu ERP. URL: o endereço acima.",
      "Clique em Conectar.",
      PASSO_PERMITIR,
    ],
  },
  {
    id: "grok", nome: "Grok", novo: true,
    requisito: "Precisa de um plano pago do Grok (SuperGrok). Faça pelo site grok.com.",
    passos: [
      "No site grok.com, abra Conectores (grok.com/connectors).",
      "Clique em Novo conector › Personalizado e cole a URL acima.",
      "Clique em Conectar.",
      PASSO_PERMITIR,
    ],
  },
  {
    id: "claude-code", nome: "Claude Code",
    requisito: "Para quem usa o Claude no terminal. Usa uma chave pessoal no lugar do login.",
    passos: [
      "Gere uma chave pessoal em Configurações › Integrações de IA › Chaves pessoais e copie.",
      "Rode no terminal, trocando SUA_CHAVE pela chave copiada:",
      'claude mcp add --transport http pharu-erp {url} --header "Authorization: Bearer SUA_CHAVE"',
    ],
  },
  {
    id: "outras", nome: "Outras IAs",
    requisito: "Cursor, VS Code e qualquer programa que aceite MCP por HTTP.",
    passos: [
      "Qualquer cliente MCP por HTTP: use a URL acima.",
      "Se ele não fizer o login pelo ERP (OAuth), gere uma chave pessoal em Configurações › Integrações de IA e mande no cabeçalho Authorization: Bearer SUA_CHAVE.",
    ],
  },
];

/** Comando de terminal (vai com botão de copiar, não como frase). */
export const ehComando = (passo) => passo.startsWith("claude mcp");

export const PASSOS_CALDAV = [
  {
    id: "iphone", nome: "iPhone e iPad",
    passos: [
      "Ajustes › Apps › Calendário › Contas de Calendário › Adicionar Conta › Outra › Adicionar Conta CalDAV (em versões antigas: Ajustes › Calendário › Contas).",
      "Preencha Servidor, Nome de usuário e Senha com os dados acima e toque em Seguinte.",
      "O calendário “Pharu ERP” aparece no app Calendário.",
    ],
  },
  {
    id: "mac", nome: "Mac",
    passos: [
      "No app Calendário: Calendário › Ajustes › Contas › + › Outra Conta CalDAV.",
      "Tipo de conta: Manual. Preencha usuário, senha e servidor com os dados acima.",
    ],
  },
  {
    id: "outros", nome: "Android e outros",
    passos: [
      "No Android, instale o DAVx⁵, escolha “Login com URL e nome de usuário” e use https://{servidor}/caldav/.",
      "No Thunderbird: Novo calendário › Na rede, com o mesmo endereço, usuário e senha.",
    ],
  },
];

/** Assinatura .ics: só ver, em qualquer calendário. */
export const PASSOS_POR_CALENDARIO = [
  {
    id: "google", nome: "Google Agenda",
    passos: [
      "No computador, abra o Google Agenda e, em “Outras agendas”, clique em + › “Do URL”.",
      "Cole o link e clique em “Adicionar agenda”. Ela aparece no celular também.",
      "O Google atualiza agenda assinada de tempos em tempos (pode levar algumas horas). Não dá para mudar esse intervalo.",
    ],
  },
  {
    id: "apple", nome: "iPhone e Mac",
    passos: [
      "No iPhone: Ajustes › Calendário › Contas › Adicionar Conta › Outra › Adicionar Calendário Assinado. Cole o link.",
      "No Mac: no app Calendário, Arquivo › Nova Assinatura de Calendário. Cole o link e escolha de quanto em quanto tempo atualizar.",
    ],
  },
  {
    id: "outlook", nome: "Outlook",
    passos: [
      "No Outlook na web: Calendário › Adicionar calendário › Assinar da Web. Cole o link e dê um nome.",
    ],
  },
];

/** Passos do Google de duas vias (o botão "Conectar" faz o resto). */
export const PASSOS_GOOGLE = [
  "Clique em Conectar o Google Agenda, logo abaixo.",
  "Escolha a sua conta Google. Se aparecer uma caixa de permissão da agenda, marque e clique em Continuar.",
  "Pronto: o calendário “Pharu ERP” aparece no Google Agenda, no computador e no app do celular, em até 1 minuto.",
];
